import { getConfigTenant, isCorpTenant } from "@capone/common";
import { useContext, useEffect } from "react";
import { createGlobalState } from "react-use";
import { fetchActiveBusinessExperiments } from "../api/corporate";
import { CorpBusinessExperimentsMap } from "../types";
import { ExperimentsHookContext } from "./ExperimentsHookProvider";

type ExperimentsState = {
  experiments: CorpBusinessExperimentsMap;
  trackingProperties: Record<string, string>;
};

const useExperimentsHook = createGlobalState<ExperimentsState | undefined>();
const useActiveBusinessExperimentsPromise = createGlobalState<
  Promise<ExperimentsState> | undefined
>(undefined);
const useExperimentsCallState = createGlobalState<"loading" | "resolved">(
  "loading"
);

export const useBusinessExperiments = () => {
  const ctx = useContext(ExperimentsHookContext);
  const isLoggedIn = Boolean(ctx?.isLoggedIn);

  const [state, setState] = useExperimentsHook();
  const [promise, setPromise] = useActiveBusinessExperimentsPromise();
  const [experimentsCallState, setExperimentsCallState] =
    useExperimentsCallState();

  const experiments = state?.experiments || ({} as CorpBusinessExperimentsMap);
  const isCorporate = isCorpTenant(getConfigTenant());
  useEffect(() => {
    // This effect will wait for auth to be set before triggering Experiments API call.
    // And sets the promise singleton so we only fetch once.
    if (isLoggedIn && !promise && isCorporate) {
      setPromise(fetchActiveBusinessExperiments());
    }
  }, [isLoggedIn, promise, isCorporate]);

  useEffect(() => {
    // Wait for the fetchActiveExperiments promise to exist, and then resolve it into experiments.
    const resolvePromiseSingleton = async () => {
      const result = await promise;
      setState(result);
      setExperimentsCallState("resolved");
    };

    if (promise) {
      resolvePromiseSingleton();
    }
  }, [promise]);

  return {
    experiments,
    experimentsCallState,
    trackingProperties: state?.trackingProperties ?? {},
  };
};
